<script setup lang="ts">
import { computed, nextTick, ref } from 'vue';

const props = withDefaults(
  defineProps<{
    value: string;
    placeholder?: string;
    editable?: boolean;
    clearable?: boolean;
  }>(),
  {
    editable: true,
    clearable: true,
  },
);

const emit = defineEmits<(e: 'onChange', value: string) => void>();

const isEditMode = ref(false);
const inputValue = ref(props.value);
const inputRef = ref<HTMLInputElement>();

const labelValue = computed(() => {
  return isEditMode.value ? inputValue.value : props.value;
});

const handleEnterEditMode = async () => {
  if (!props.editable) {
    return;
  }
  inputValue.value = props.value;
  isEditMode.value = true;
  await nextTick();
  inputRef.value?.focus();
};

const handleLeaveEditMode = () => {
  isEditMode.value = false;

  if (
    (!props.clearable && inputValue.value === '') ||
    inputValue.value === props.value
  ) {
    return;
  }

  emit('onChange', inputValue.value);
};

const handleChange = (e: Event) => {
  inputValue.value = (e.target as HTMLInputElement).value;
};

const handleInput = (e: Event) => {
  inputValue.value = (e.target as HTMLInputElement).value;
};
</script>

<template>
  <input
    ref="inputRef"
    v-if="isEditMode"
    :value="inputValue"
    :placeholder="placeholder"
    class="max-w-full rounded-md p-1 text-base font-bold"
    :class="{
      'focus:border-primary-500 cursor-pointer focus-within:cursor-text hover:bg-neutral-50 focus:border focus:bg-white':
        editable,
    }"
    @change="handleChange"
    @blur="handleLeaveEditMode"
    @input="handleInput"
    @keydown.enter="handleLeaveEditMode"
  />
  <label
    @focus="handleEnterEditMode"
    class="inline max-w-full overflow-hidden rounded-md p-1 text-base font-bold text-ellipsis whitespace-nowrap default:min-w-36"
    :class="{
      invisible: isEditMode,
      [isEditMode ? 'h-0 py-0' : 'h-8 py-1']: true,
    }"
    tabindex="0"
    >{{ labelValue }}</label
  >
</template>
